/**
 * 합치기 확인창 — PN 정정(제품 상세) · 승격(요청 상세)의 409 가 연다(합칠 후보 탭은 묶음 창 — merge-group-dialog).
 * 열면 재료(미리보기 · 짝 상세 · 없어질 제품 상세)를 서버에서 지금 값으로 다시 받는다 — 부르는 쪽이 쥔 값은 낡았을 수 있다.
 * 창은 짝(남을 제품)으로 갈래를 정해 그 말로 묻는다 — 카탈로그 있음 · 같은 파트넘버로 요청 중(백엔드 V장 "확인창 다시 쓰기 — 409 의 창", 2026-10-30).
 * 창은 document.body 에 붙였다가 닫으면 뗀다. 상태는 이 창 안에서만 산다(부르는 뷰의 data 를 건드리지 않는다).
 * 합쳤을 때 다음 걸음(상세로 옮겨 감 · 다시 승격)은 부르는 쪽이 정한다.
 */
import { escapeHtml } from '../lib/cells';
import { fetchMergePreview, fetchProduct, mergeProducts, type MergeCount, type MergePreview, type ProductDetail } from '../api/product-api';

/** 창을 연 자리 — PN 정정(제품 상세)은 없어질 제품의 열린 요청을 닫고, 승격(요청 상세)은 그 요청으로 다시 승격하므로 안 닫는다(2026-10-30) */
export type EMergeOrigin = 'pn_change' | 'promote';

/** 창의 갈래 — 짝(남을 제품)이 카탈로그에 이어졌으면 catalog, 아니면 request(같은 파트넘버로 요청 중) */
type MergeDialogCase = 'catalog' | 'request';

/** 창 하나의 상태 — 재료(미리보기 · 짝 상세 · 없어질 제품 상세)를 받는 중 · 합치는 중 · 실패 글 · 카테고리가 다를 때의 "그래도 같은 물건입니다" 체크 */
type MergeDialogState = {
  preview: MergePreview | null;
  keep: ProductDetail | null;
  drop: ProductDetail | null;
  loading: boolean;
  merging: boolean;
  bSameThingChecked: boolean;
  error: string;
};

const TITLE_WORD: Record<MergeDialogCase, string> = { catalog: '카탈로그에 이미 있습니다', request: '같은 파트넘버로 요청된 건이 있습니다' };
const CONFIRM_WORD: Record<MergeDialogCase, string> = { catalog: '이 카탈로그에 연결', request: '삭제' };
const NO_CATEGORY_WORD = '카테고리 없음';


/** 창을 열고 사람의 답을 기다린다. 합쳤으면 남은 제품의 상세, 취소 · 바깥 · Esc 면 null */
export async function openMergeDialog(keepId: string, dropId: string, origin: EMergeOrigin): Promise<ProductDetail | null> {
  const host = document.createElement('div');
  const state: MergeDialogState = { preview: null, keep: null, drop: null, loading: true, merging: false, bSameThingChecked: false, error: '' };
  const draw = (): void => { host.innerHTML = renderMergeDialog(state); };
  document.body.appendChild(host);
  draw();

  return new Promise((resolve) => {
    function close(result: ProductDetail | null): void {
      document.removeEventListener('keydown', handleKey);
      host.remove();
      resolve(result);
    }
    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape' && !state.merging) close(null);
    }
    async function handleMerge(): Promise<void> {
      state.merging = true;
      state.error = '';
      draw();
      try {
        close(await mergeProducts(keepId, dropId, origin === 'pn_change'));
      } catch (e) {
        state.merging = false;   // 막힌 이유(409)는 서버 글 그대로 창에 남는다
        state.error = e instanceof Error ? e.message : String(e);
        draw();
      }
    }
    function handleClick(event: MouseEvent): void {
      const target = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (target === null) return;
      if (target.dataset.action === 'merge-cancel' && !state.merging) close(null);
      if (target.dataset.action === 'merge-confirm') void handleMerge();
      if (target.dataset.action === 'merge-same-thing') {
        state.bSameThingChecked = (target as HTMLInputElement).checked;
        draw();
      }
    }

    host.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKey);
    Promise.all([fetchMergePreview(keepId, dropId), fetchProduct(keepId), fetchProduct(dropId)])
      .then(([preview, keep, drop]) => {
        state.preview = preview;
        state.keep = keep;
        state.drop = drop;
      })
      .catch((e: unknown) => { state.error = e instanceof Error ? e.message : String(e); })
      .finally(() => { state.loading = false; draw(); });
  });
}

/** 창 전체 — 갈래의 제목 · 안내 · 짝 카드 · 옮김 줄(있을 때만) · 카테고리 다름 체크 · 막힌 이유 · 실패 글 · [취소] [갈래 버튼] */
function renderMergeDialog(state: MergeDialogState): string {
  const bReady = state.preview !== null && state.keep !== null && state.drop !== null;
  const mergeCase: MergeDialogCase | null = state.keep === null ? null : mergeDialogCaseOf(state.keep);
  const title = mergeCase === null ? '제품 합치기' : TITLE_WORD[mergeCase];
  const confirmWord = state.merging ? '처리 중…' : mergeCase === null ? '합치기' : CONFIRM_WORD[mergeCase];
  const body = state.loading ? '<p class="mg-note">불러오는 중…</p>' : bReady ? renderMergeBody(state) : '';
  return `
    <div class="mg-wrap" role="dialog" aria-label="${escapeHtml(title)}">
      <div class="mg-overlay" data-action="merge-cancel"></div>
      <div class="mg-dialog">
        <h3 class="mg-title">${escapeHtml(title)}</h3>
        ${body}
        ${state.error === '' ? '' : `<p class="text-danger">${escapeHtml(state.error)}</p>`}
        <div class="mg-buttons">
          <button type="button" class="btn-ghost" data-action="merge-cancel" ${state.merging ? 'disabled' : ''}>취소</button>
          <button type="button" class="btn-primary" data-action="merge-confirm" ${canConfirm(state) ? '' : 'disabled'}>${escapeHtml(confirmWord)}</button>
        </div>
      </div>
    </div>`;
}

/** 재료가 다 왔을 때의 몸 — 갈래마다 안내 · 카드가 다르고, 옮김 · 카테고리 · 막힘 줄은 같다 */
function renderMergeBody(state: MergeDialogState): string {
  const { preview, keep, drop } = state;
  if (preview === null || keep === null || drop === null) return '';
  const moved = movedWordOf(preview.counts);
  const lead = mergeDialogCaseOf(keep) === 'catalog'
    ? `<p class="mg-note">내용이 맞으면 이 제품(${escapeHtml(drop.hclt_number)})을 그 카탈로그에 연결합니다.</p>${renderCatalogCard(keep)}`
    : `<p class="mg-note">${escapeHtml(categoryWordOf(keep))}에서 같은 파트넘버로 요청된 건이 있습니다 — 이 요청은 삭제할까요?</p>${renderRequestCard(keep)}`;
  return `${lead}
        ${moved === '' ? '' : `<p class="mg-units">${escapeHtml(moved)}</p>`}
        ${isCategoryMismatch(keep, drop) ? renderMismatch(keep, drop, state.bSameThingChecked) : ''}
        ${preview.blocked_reason === null ? '' : `<p class="mg-blocked">합칠 수 없습니다 — ${escapeHtml(preview.blocked_reason)}</p>`}`;
}

/** 카탈로그 갈래의 카드 — 짝이 카탈로그에서 받은 값: 파트넘버 · 디스크립션 · 카테고리 · 제조사 */
function renderCatalogCard(keep: ProductDetail): string {
  return `
        <div class="mg-side">
          <div class="mg-pn">${escapeHtml(textOrDash(keep.option_part))}</div>
          <div class="mg-desc">${escapeHtml(textOrDash(keep.description))}</div>
          <div class="mg-number">${escapeHtml(categoryWordOf(keep))} · ${escapeHtml(textOrDash(keep.vendor))}</div>
        </div>`;
}

/** 요청 갈래의 카드 — 같은 파트넘버로 요청 중인 짝: 파트넘버 · 디스크립션 · 제품번호 */
function renderRequestCard(keep: ProductDetail): string {
  return `
        <div class="mg-side">
          <div class="mg-pn">${escapeHtml(textOrDash(keep.option_part))}</div>
          <div class="mg-desc">${escapeHtml(textOrDash(keep.description))}</div>
          <div class="mg-number">${escapeHtml(keep.hclt_number)}</div>
        </div>`;
}

/** 카테고리가 다를 때 — 두 카테고리와 "그래도 같은 물건입니다" 체크(켜야 버튼이 열린다) */
function renderMismatch(keep: ProductDetail, drop: ProductDetail, bChecked: boolean): string {
  return `
        <div class="mg-blocked">
          카테고리가 다릅니다 — 이 제품 ${escapeHtml(categoryWordOf(drop))} · 그쪽 ${escapeHtml(categoryWordOf(keep))}
          <label><input type="checkbox" data-action="merge-same-thing" ${bChecked ? 'checked' : ''}> 그래도 같은 물건입니다</label>
        </div>`;
}

/** 버튼이 열리는가 — 재료가 다 왔고 · 막힘이 없고 · 처리 중이 아니고 · 카테고리가 같거나 체크가 켜졌을 때 */
function canConfirm(state: MergeDialogState): boolean {
  const { preview, keep, drop } = state;
  if (preview === null || keep === null || drop === null) return false;
  const bCategoryOk = !isCategoryMismatch(keep, drop) || state.bSameThingChecked;
  return preview.blocked_reason === null && !state.merging && bCategoryOk;
}

/** 갈래 — 짝이 카탈로그에 이어졌으면 catalog, 아니면 request */
function mergeDialogCaseOf(keep: ProductDetail): MergeDialogCase {
  return keep.catalog === 'linked' ? 'catalog' : 'request';
}

/** 카테고리가 다른가 — 둘 다 있을 때만 견준다(서버 · 세트는 null) */
function isCategoryMismatch(keep: ProductDetail, drop: ProductDetail): boolean {
  return keep.spec_category !== null && drop.spec_category !== null && keep.spec_category !== drop.spec_category;
}

/** 옮김 줄 — 실물 · 견적이 붙어 있을 때만. "실물 3장 · 견적 2건은 그쪽으로 옮겨집니다." 둘 다 0 이면 '' */
function movedWordOf(counts: MergeCount): string {
  const parts: string[] = [];
  if (counts.units > 0) parts.push(`실물 ${counts.units}장`);
  if (counts.quote_items > 0) parts.push(`견적 ${counts.quote_items}건`);
  return parts.length === 0 ? '' : `${parts.join(' · ')}은 그쪽으로 옮겨집니다.`;
}

function categoryWordOf(product: ProductDetail): string {
  return product.spec_category === null ? NO_CATEGORY_WORD : product.spec_category;
}

function textOrDash(text: string | null): string {
  return text === null ? '—' : text;
}
